import { ChangeDetectionStrategy, Component } from '@angular/core';
import { isNil } from '@fmnts/core';
import { TemplatePart } from '@fmnts/declarative-forms';
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons';
import {
  MonoTypeOperatorFunction,
  Observable,
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
} from 'rxjs';
import { SharedDeclarativeCampaignFormsModule } from '../../shared-declarative-next-forms.module';
import { TextSummaryFieldComponent } from '../text-summary-field/text-summary-field.component';

/**
 * Component for displaying the summary of the payment information
 */
@Component({
  selector: 'next-payment-summary-field',
  templateUrl: './payment-summary-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [SharedDeclarativeCampaignFormsModule],
})
export class PaymentSummaryFieldComponent extends TextSummaryFieldComponent {
  /** @internal */
  protected _iconHeader = faCheckCircle;

  protected override transformTemplate(): MonoTypeOperatorFunction<string> {
    return (s) =>
      s.pipe(
        super.transformTemplate(),
        switchMap((content) => this._prepareTemplateFromFormValues(content)),
      );
  }

  protected override transformInterpolated(
    part: TemplatePart,
  ): MonoTypeOperatorFunction<string> {
    return (value$) =>
      value$.pipe(
        // For the payment summary the text for missing
        // values should be `-` instead of an empty string
        map((v) => (isNil(v) || v === '' ? '-' : v)),
        super.transformInterpolated(part),
      );
  }

  /**
   * @param content Original template content
   *
   * @returns
   * An `Observable` that transforms the given `content`
   * based on the current form values.
   */
  private _prepareTemplateFromFormValues(content: string): Observable<string> {
    const { form } = this;
    const contentForOneOff = this._templateForOneOff(content);

    // Based on changes to the chosen interval this will
    // either emit the "full" payment summary or the one
    // without the recurring contribution
    return form.flatValueChanges.pipe(
      startWith(form.flatValue),
      debounceTime(0),
      map((values) => values['direct_debit_interval'] === '4'),
      distinctUntilChanged(),
      map((isOneOff) => (isOneOff ? contentForOneOff : content)),
    );
  }

  /**
   * Transforms the given `template` to skip the line for
   * recurring contribution.
   *
   * @param template Template string
   * @returns
   */
  private _templateForOneOff(template: string) {
    // Regex that checks if a given line contains {{donation_amount}}
    // as this should be removed when using one-off payments
    const regexForLineToRemove = /\{\{\s*donation_amount\s*\}\}/;

    return template
      .split(/\n/)
      .filter((line) => !regexForLineToRemove.test(line))
      .join('\n');
  }
}
